import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { bsToAd, type BsDate } from '@ministry/nepali-date';
import {
  AlertBanner,
  Button,
  Card,
  Combobox,
  DatePicker,
  EmptyState,
  QrCode,
  Skeleton,
  Stepper,
  Textarea,
  TextInput,
  type StepperStep,
} from '@ministry/ui/internal';

import { getFileQr, listFiles, registerFile, type FileOrgUnitRef, type RegisterFileInput, type RegisterFileResult } from '../lib/files.js';
import { listMovements } from '../lib/workflow.js';
import { ApiError, NetworkError } from '../lib/api-client.js';
import { generateIdempotencyKey } from '../lib/idempotency.js';
import { trackingQrUrl } from '../lib/qr.js';
import { useSession } from '../lib/session.js';
import { ROUTES } from '../lib/nav.js';
import { useT } from '../i18n/strings.js';
import { PageHeader } from '../components/PageHeader.js';

const PHONE_PATTERN = /^(\+977)?9[678][0-9]{8}$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Step = 'applicant' | 'file' | 'review';
const STEP_ORDER: Step[] = ['applicant', 'file', 'review'];

interface FormState {
  fullName: string;
  phone: string;
  email: string;
  addressText: string;
  subject: string;
  sectionId: string;
  dartaNumber: string;
  chalaniNumber: string;
  registerDate: BsDate | undefined;
}

const EMPTY_FORM: FormState = {
  fullName: '',
  phone: '',
  email: '',
  addressText: '',
  subject: '',
  sectionId: '',
  dartaNumber: '',
  chalaniNumber: '',
  registerDate: undefined,
};

/**
 * SCREEN_SPECS.md I7 — the most conspicuous gap this build closes: without this screen a
 * file-tracking system cannot register a file at all.
 *
 * **Adapted from the spec's literal 4-step order** (Applicant → File details → Attachments
 * → Review): `POST /files/{id}/attachments` requires a `fileId`, and no file exists until
 * this form's final submit succeeds — a step that asks for an attachment before the file
 * is created cannot call the real endpoint. Attachments are added on the File Detail
 * screen immediately after registration instead (this build's own attachments upload,
 * wired the same round), which is the honest sequencing the API actually supports rather
 * than a step that could only ever be decorative.
 *
 * **`sectionId` has no `/reference/org-units` endpoint to ask** — `internal-api` mounts no
 * `reference`/`org` module in this build (confirmed by reading `entrypoints/internal-api/
 * routes.ts`). This screen sources its section list from the distinct `section` values on
 * `GET /files` (a real, permission-gated, tenant-scoped endpoint every registering officer
 * already has access to), which is honest given what exists but is NOT the authoritative
 * org chart and will miss a section with zero files registered in it yet — flagged loudly
 * in the handback as the real gap for the backend agent to close.
 */
export function FileIntakePage(): React.JSX.Element {
  const t = useT();
  const navigate = useNavigate();
  const { data: session } = useSession();

  // Named defect ("no proper back navigation in nested views"): this screen is reached
  // two ways — the primary nav's own "File Intake" item, AND File Register's "New file"
  // button — so it is a genuine child of the register even though it also has its own
  // top-level route. One breadcrumb, reused across every return path below (forbidden /
  // success / the form itself), rather than three screens that could each say something
  // slightly different about "where this page sits".
  const breadcrumb = [
    { label: t('shell.dashboard'), href: ROUTES.dashboard },
    { label: t('fileRegister.title'), href: ROUTES.files },
    { label: t('fileIntake.title') },
  ];

  const [step, setStep] = React.useState<Step>('applicant');
  const [form, setForm] = React.useState<FormState>(EMPTY_FORM);
  const [touched, setTouched] = React.useState<Record<string, boolean>>({});
  const [result, setResult] = React.useState<RegisterFileResult | null>(null);

  /**
   * R-035's rule, applied here exactly as the handback asks: minted ONCE when this form's
   * intent begins (mount) and reused across every retry of this same submission, so a
   * dropped connection or a second click on "Register file" cannot mint a code twice.
   */
  const idempotencyKeyRef = React.useRef<string>(generateIdempotencyKey());

  const sectionsQuery = useQuery({
    queryKey: ['file-intake', 'known-sections'],
    /**
     * `GET /files`'s own `section` field is NOT reliably section-level: it is the file's
     * CURRENT holder's org unit, which drifts up the hierarchy the moment a file is
     * forwarded to a division- or ministry-level officer (VERIFIED against a running
     * server: one seeded file's `section` came back as `{nameEn: "Sample Ministry"}`, the
     * tenant's ROOT, not a section — picking it as `sectionId` here 422s on `POST /files`,
     * since AC-6 requires the registration section specifically). The `register` movement
     * (always `seqNo` 1, the first page of `GET /files/{id}/movements`) is the one place
     * the ACTUAL registration section is recorded, so this queries that instead — at the
     * cost of one extra request per candidate file. Bounded to the 15 most recently
     * registered files so a busy tenant's page load stays reasonable. **Still misses a
     * section with zero registered files** — the real fix is `GET /reference/org-units`
     * filtered to `scopeLevel=section`, which does not exist in this build; flagged in the
     * handback.
     */
    queryFn: async ({ signal }) => {
      const { rows } = await listFiles({ pageSize: 15, sort: '-registeredAt' }, signal);
      /**
       * VERIFIED against a running server, and a real finding for the handback:
       * `GET /files` returned rows this same caller then got `403 FORBIDDEN_SCOPE` from
       * on `GET /files/{id}/movements` (AC-35) — the register list's own row-scoping and
       * the movements endpoint's per-record scope check disagree for the same actor.
       * `allSettled` rather than `all` so one scope-denied candidate does not fail this
       * whole best-effort lookup for the others.
       */
      const firstMovements = await Promise.allSettled(rows.map((row) => listMovements(row.id, { limit: 1 }, signal)));
      const bySection = new Map<string, FileOrgUnitRef>();
      for (const outcome of firstMovements) {
        if (outcome.status !== 'fulfilled') continue;
        const registerMovement = outcome.value.items.find((m) => m.action.code === 'register');
        const orgUnit = registerMovement?.to;
        if (orgUnit?.orgUnitId && orgUnit.orgUnitNameNe && orgUnit.orgUnitNameEn) {
          bySection.set(orgUnit.orgUnitId, { id: orgUnit.orgUnitId, nameNe: orgUnit.orgUnitNameNe, nameEn: orgUnit.orgUnitNameEn });
        }
      }
      return [...bySection.values()];
    },
  });

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm((prev) => ({ ...prev, [key]: value }));
  const touch = (key: string) => setTouched((prev) => ({ ...prev, [key]: true }));

  const phoneError = touched.phone && form.phone !== '' && !PHONE_PATTERN.test(form.phone) ? t('fileIntake.applicant.phone.error') : undefined;
  const phoneMissing = touched.phone && form.phone === '';
  const fullNameMissing = touched.fullName && form.fullName.trim() === '';
  const emailError = touched.email && form.email !== '' && !EMAIL_PATTERN.test(form.email) ? t('fileIntake.applicant.email.error') : undefined;

  const subjectError =
    touched.subject && (form.subject.trim().length < 3 || form.subject.length > 500) ? t('fileIntake.file.subject.error') : undefined;
  const subjectMissing = touched.subject && form.subject.trim() === '';
  const sectionMissing = touched.sectionId && form.sectionId === '';

  const applicantValid = form.fullName.trim() !== '' && PHONE_PATTERN.test(form.phone) && (form.email === '' || EMAIL_PATTERN.test(form.email));
  const fileValid = form.subject.trim().length >= 3 && form.subject.length <= 500 && form.sectionId !== '';

  const mutation = useMutation({
    mutationFn: () => {
      const input: RegisterFileInput = {
        subject: form.subject.trim(),
        sectionId: form.sectionId,
        applicant: {
          fullName: form.fullName.trim(),
          phone: form.phone.trim(),
          email: form.email.trim() === '' ? null : form.email.trim(),
          addressText: form.addressText.trim() === '' ? null : form.addressText.trim(),
        },
        darta:
          form.dartaNumber.trim() === '' && form.chalaniNumber.trim() === '' && !form.registerDate
            ? null
            : {
                dartaNumber: form.dartaNumber.trim() === '' ? null : form.dartaNumber.trim(),
                chalaniNumber: form.chalaniNumber.trim() === '' ? null : form.chalaniNumber.trim(),
                registerDate: form.registerDate ? isoFromBs(form.registerDate) : null,
              },
      };
      return registerFile(input, idempotencyKeyRef.current);
    },
    onSuccess: (value) => setResult(value),
  });

  /**
   * `RegisterFileResult` already carries `qrToken`, but not the `qrFragmentPath` §14.1
   * defines the exact `/t#<token>` shape for — fetched once from the authoritative
   * `GET /files/{id}/qr` rather than this screen re-deriving the `/t#` prefix itself,
   * so a future change to that shape only has one place to update.
   */
  const qrQuery = useQuery({
    queryKey: ['file-qr', result?.id],
    queryFn: ({ signal }) => getFileQr(result?.id as string, signal),
    enabled: result !== null,
  });

  const goNext = () => {
    if (step === 'applicant') {
      setTouched((prev) => ({ ...prev, fullName: true, phone: true, email: true }));
      if (!applicantValid) return;
      setStep('file');
      return;
    }
    if (step === 'file') {
      setTouched((prev) => ({ ...prev, subject: true, sectionId: true }));
      if (!fileValid) return;
      setStep('review');
      return;
    }
  };

  const goBack = () => {
    const idx = STEP_ORDER.indexOf(step);
    if (idx > 0) setStep(STEP_ORDER[idx - 1] as Step);
  };

  const steps: StepperStep[] = STEP_ORDER.map((s) => ({
    label: t(`fileIntake.step.${s}`),
    status: s === step ? 'active' : STEP_ORDER.indexOf(s) < STEP_ORDER.indexOf(step) ? 'completed' : 'upcoming',
  }));

  const section = sectionsQuery.data?.find((s) => s.id === form.sectionId);

  // Gated on `permissions` from `/auth/me`, never on `roleCode` (CLAUDE.md's brief). A
  // role without `file.register` (HR, AUD, SA, and most JS/SEC assignments per
  // SCREEN_SPECS.md I7's "Visible to" list) sees a stated reason, never a form it cannot
  // submit — the server would 403 on `POST /files` regardless, but failing at submit
  // after three steps of data entry is the "discovered through failure" anti-pattern
  // `ux-review` names directly.
  if (session && !session.permissions.includes('file.register')) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title={t('fileIntake.title')} breadcrumb={breadcrumb} />
        <AlertBanner variant="warning" title={t('fileIntake.forbidden.title')}>
          {t('fileIntake.forbidden.body')}
        </AlertBanner>
      </div>
    );
  }

  const errorBanner = (() => {
    const error = mutation.error;
    if (!error) return null;
    if (error instanceof ApiError) {
      if (error.code === 'VALIDATION_FAILED' && error.fieldErrors.some((fe) => fe.field === 'sectionId')) {
        return <AlertBanner variant="critical" title={t('fileIntake.error.sectionScope')} />;
      }
      if (error.code === 'VALIDATION_FAILED') {
        return (
          <AlertBanner variant="critical" title={t('fileIntake.error.title')}>
            {t('fileIntake.error.body')}
          </AlertBanner>
        );
      }
      if (error.retryable) {
        return (
          <AlertBanner
            variant="critical"
            title={t('fileIntake.error.network')}
            action={
              <Button size="sm" variant="secondary" onClick={() => mutation.mutate()}>
                {t('common.retry')}
              </Button>
            }
          />
        );
      }
    }
    if (error instanceof NetworkError) {
      return (
        <AlertBanner
          variant="critical"
          title={t('fileIntake.error.network')}
          action={
            <Button size="sm" variant="secondary" onClick={() => mutation.mutate()}>
              {t('common.retry')}
            </Button>
          }
        />
      );
    }
    return (
      <AlertBanner variant="critical" title={t('fileIntake.error.title')}>
        {t('fileIntake.error.body')}
      </AlertBanner>
    );
  })();

  if (result) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader title={t('fileIntake.title')} breadcrumb={breadcrumb} />
        <Card data-print-area className="flex flex-col gap-4 border-status-good-solid">
          <h2 className="text-lg font-semibold text-status-good-tint-fg">{t('fileIntake.success.title')}</h2>
          <p className="text-text-primary">{t('fileIntake.success.body', { code: result.trackingCode })}</p>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
            {qrQuery.isLoading ? (
              <Skeleton shape="block" className="h-44 w-44 shrink-0" />
            ) : qrQuery.isError ? (
              <AlertBanner
                variant="warning"
                title={t('fileIntake.success.qrError')}
                action={
                  <Button size="sm" variant="secondary" onClick={() => qrQuery.refetch()}>
                    {t('common.retry')}
                  </Button>
                }
              />
            ) : qrQuery.data ? (
              <QrCode
                value={trackingQrUrl(qrQuery.data.qrFragmentPath)}
                label={t('fileIntake.success.qrLabel', { code: result.trackingCode })}
                className="shrink-0"
              />
            ) : null}
            <dl className="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2">
              <div>
                <dt className="text-sm text-text-muted">{t('fileIntake.success.trackingCode')}</dt>
                <dd className="font-mono text-lg text-text-heading">{result.trackingCode}</dd>
              </div>
              <div>
                <dt className="text-sm text-text-muted">{t('fileIntake.success.qrToken')}</dt>
                <dd className="break-all font-mono text-sm text-text-primary">{result.qrToken}</dd>
              </div>
            </dl>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => navigate(ROUTES.fileDetail(result.id))}>{t('fileIntake.success.viewFile')}</Button>
            <Button variant="secondary" onClick={() => window.print()} disabled={!qrQuery.data}>
              {t('fileIntake.success.print')}
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setResult(null);
                setForm(EMPTY_FORM);
                setTouched({});
                setStep('applicant');
                idempotencyKeyRef.current = generateIdempotencyKey();
              }}
            >
              {t('fileIntake.newFile')}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title={t('fileIntake.title')} description={t('fileIntake.subtitle')} breadcrumb={breadcrumb} />
      <Stepper steps={steps} />

      {errorBanner}

      {step === 'applicant' ? (
        <Card className="flex flex-col gap-4">
          <TextInput
            id="intake-fullname"
            label={t('fileIntake.applicant.fullName')}
            value={form.fullName}
            onChange={(e) => set('fullName', e.target.value)}
            onBlur={() => touch('fullName')}
            errorText={fullNameMissing ? t('fileIntake.applicant.required') : undefined}
            required
          />
          <TextInput
            id="intake-phone"
            label={t('fileIntake.applicant.phone')}
            helperText={phoneError || phoneMissing ? undefined : t('fileIntake.applicant.phone.helper')}
            value={form.phone}
            inputMode="tel"
            onChange={(e) => set('phone', e.target.value.trim())}
            onBlur={() => touch('phone')}
            errorText={phoneMissing ? t('fileIntake.applicant.required') : phoneError}
            required
          />
          <TextInput
            id="intake-email"
            label={t('fileIntake.applicant.email')}
            value={form.email}
            onChange={(e) => set('email', e.target.value)}
            onBlur={() => touch('email')}
            errorText={emailError}
          />
          <Textarea
            id="intake-address"
            label={t('fileIntake.applicant.address')}
            value={form.addressText}
            onChange={(e) => set('addressText', e.target.value)}
          />
          <div className="flex justify-end">
            <Button onClick={goNext}>{t('fileIntake.nav.next')}</Button>
          </div>
        </Card>
      ) : null}

      {step === 'file' ? (
        <Card className="flex flex-col gap-4">
          <Textarea
            id="intake-subject"
            label={t('fileIntake.file.subject')}
            helperText={subjectError || subjectMissing ? undefined : t('fileIntake.file.subject.helper')}
            value={form.subject}
            onChange={(e) => set('subject', e.target.value)}
            onBlur={() => touch('subject')}
            errorText={subjectMissing ? t('fileIntake.applicant.required') : subjectError}
            required
          />

          {sectionsQuery.isLoading ? (
            <Skeleton shape="list-row" count={1} />
          ) : sectionsQuery.isError ? (
            <AlertBanner
              variant="critical"
              title={t('common.somethingWentWrong')}
              action={
                <Button size="sm" variant="secondary" onClick={() => sectionsQuery.refetch()}>
                  {t('common.retry')}
                </Button>
              }
            />
          ) : (sectionsQuery.data ?? []).length === 0 ? (
            <EmptyState title={t('fileIntake.file.section.empty')} body={t('fileIntake.file.section.helper')} />
          ) : (
            <Combobox
              id="intake-section"
              label={t('fileIntake.file.section')}
              placeholder={t('fileIntake.file.section.placeholder')}
              helperText={sectionMissing ? undefined : t('fileIntake.file.section.helper')}
              options={(sectionsQuery.data ?? []).map((s) => ({ value: s.id, label: `${s.nameNe} · ${s.nameEn}` }))}
              value={form.sectionId || undefined}
              onValueChange={(value) => {
                set('sectionId', value);
                touch('sectionId');
              }}
              errorText={sectionMissing ? t('fileIntake.file.section.required') : undefined}
              required
            />
          )}

          <fieldset className="flex flex-col gap-3 rounded-md border border-border-decorative p-3">
            <legend className="px-1 text-sm font-semibold text-text-primary">{t('fileIntake.darta.title')}</legend>
            <p className="text-sm text-text-muted">{t('fileIntake.darta.helper')}</p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <TextInput
                id="intake-darta-number"
                label={t('fileIntake.darta.dartaNumber')}
                value={form.dartaNumber}
                onChange={(e) => set('dartaNumber', e.target.value)}
              />
              <TextInput
                id="intake-chalani-number"
                label={t('fileIntake.darta.chalaniNumber')}
                value={form.chalaniNumber}
                onChange={(e) => set('chalaniNumber', e.target.value)}
              />
            </div>
            <DatePicker
              id="intake-darta-date"
              label={t('fileIntake.darta.registerDate')}
              value={form.registerDate}
              onChange={(date) => set('registerDate', date)}
            />
          </fieldset>

          <div className="flex justify-between">
            <Button variant="ghost" onClick={goBack}>
              {t('fileIntake.nav.back')}
            </Button>
            <Button onClick={goNext} disabled={(sectionsQuery.data ?? []).length === 0}>
              {t('fileIntake.nav.next')}
            </Button>
          </div>
        </Card>
      ) : null}

      {step === 'review' ? (
        <Card className="flex flex-col gap-4">
          <AlertBanner variant="warning" title={t('fileIntake.review.title')}>
            {t('fileIntake.review.body')}
          </AlertBanner>

          <div>
            <h3 className="text-sm font-semibold text-text-muted">{t('fileIntake.review.applicant')}</h3>
            <dl className="grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-2">
              <div>
                <dt className="text-sm text-text-muted">{t('fileIntake.applicant.fullName')}</dt>
                <dd className="text-text-primary">{form.fullName}</dd>
              </div>
              <div>
                <dt className="text-sm text-text-muted">{t('fileIntake.applicant.phone')}</dt>
                <dd className="text-text-primary">{form.phone}</dd>
              </div>
              <div>
                <dt className="text-sm text-text-muted">{t('fileIntake.applicant.email')}</dt>
                <dd className="text-text-primary">{form.email || t('fileDetail.notProvided')}</dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="text-sm text-text-muted">{t('fileIntake.applicant.address')}</dt>
                <dd className="text-text-primary">{form.addressText || t('fileDetail.notProvided')}</dd>
              </div>
            </dl>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-text-muted">{t('fileIntake.review.file')}</h3>
            <dl className="grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <dt className="text-sm text-text-muted">{t('fileIntake.file.subject')}</dt>
                <dd className="text-text-primary">{form.subject}</dd>
              </div>
              <div>
                <dt className="text-sm text-text-muted">{t('fileIntake.file.section')}</dt>
                <dd className="text-text-primary">{section ? `${section.nameNe} · ${section.nameEn}` : form.sectionId}</dd>
              </div>
            </dl>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-text-muted">{t('fileIntake.review.darta')}</h3>
            {form.dartaNumber || form.chalaniNumber || form.registerDate ? (
              <dl className="grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-2">
                <div>
                  <dt className="text-sm text-text-muted">{t('fileIntake.darta.dartaNumber')}</dt>
                  <dd className="text-text-primary">{form.dartaNumber || t('fileDetail.notProvided')}</dd>
                </div>
                <div>
                  <dt className="text-sm text-text-muted">{t('fileIntake.darta.chalaniNumber')}</dt>
                  <dd className="text-text-primary">{form.chalaniNumber || t('fileDetail.notProvided')}</dd>
                </div>
              </dl>
            ) : (
              <p className="text-text-muted">{t('fileIntake.review.dartaNone')}</p>
            )}
          </div>

          <div className="flex justify-between">
            <Button variant="ghost" onClick={goBack} disabled={mutation.isPending}>
              {t('fileIntake.nav.back')}
            </Button>
            <Button onClick={() => mutation.mutate()} loading={mutation.isPending} loadingLabel={t('fileIntake.nav.submitting')}>
              {t('fileIntake.nav.submit')}
            </Button>
          </div>
        </Card>
      ) : null}
    </div>
  );
}

function isoFromBs(bs: BsDate): string {
  const ad = bsToAd(bs);
  return `${String(ad.year).padStart(4, '0')}-${String(ad.month).padStart(2, '0')}-${String(ad.day).padStart(2, '0')}`;
}
